/**
 * Admin · Evidence Governance™ Console
 *
 * The governance surface for the Evidence Enrichment Framework. Review
 * web-researched copy before learners see it, inspect the immutable version
 * history, read contradictions and trust scores, run batches under an approved
 * budget, and trace any item from candidate selection to promotion.
 *
 * Governance: writes never touch the governed registry, and nothing on this
 * page influences scoring, psychometrics, Career DNA™ or market intelligence.
 * Only CONFIDENT, conflict-free, ≥80%-covered evidence can be promoted.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, Ban, Check, FileJson, FileText, Globe, History, Loader2,
  Pause, Play, Quote, RefreshCw, RotateCcw, Save, ShieldCheck, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DomainTrustBulkEditor from "@/components/admin/DomainTrustBulkEditor";
import {
  buildRoleExport, downloadRoleExportJson, downloadRoleExportPdf,
} from "@/lib/evidenceExport";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { loadCareerIntel, type CareerIntel } from "@/lib/careerIntel";
import { ENRICHED_FIELD_LABELS, type CareerEnrichmentFieldKey } from "@/lib/careerEnrichment";
import {
  estimateRun, forecastMonthlyCost, listDailyCost, listDomainTrust, listEnrichmentAudit,
  listEnrichmentCandidates, listEnrichmentTrace, listEnrichmentVersions, listEnrichments,
  listTopDomains, loadEnrichmentConfig, loadEnrichmentStats, loadReviewMetrics,
  reviewEnrichment, runBatches, runEnrichment, saveEnrichmentConfig, saveReviewNotes,
  type DailyCost, type DomainTrustRule, type EnrichmentAuditEntry, type EnrichmentCandidate,
  type EnrichmentConfig, type EnrichmentRow, type EnrichmentStats, type EnrichmentVersion,
  type DomainUse, type ReviewMetrics, type RunEstimate, type TraceEntry,
} from "@/lib/evidenceEnrichment";

type TabKey =
  | "overview" | "candidates" | "queued" | "running" | "completed"
  | "rejected" | "needs_review" | "blocked" | "conflicts" | "sources"
  | "trust" | "cost" | "governance" | "trace" | "history";

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "candidates", label: "Candidates" },
  { key: "queued", label: "Queued" },
  { key: "running", label: "Running" },
  { key: "completed", label: "Completed" },
  { key: "needs_review", label: "Needs review" },
  { key: "conflicts", label: "Contradictions" },
  { key: "rejected", label: "Rejected" },
  { key: "blocked", label: "Blocked" },
  { key: "sources", label: "Sources" },
  { key: "trust", label: "Domain trust" },
  { key: "cost", label: "Cost" },
  { key: "governance", label: "Review metrics" },
  { key: "trace", label: "Audit trail" },
  { key: "history", label: "History" },
];

const GRADE_TONE: Record<string, string> = {
  confident: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  medium: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  weak: "bg-rose-500/15 text-rose-400 border-rose-500/30",
};

const FRESHNESS_TONE: Record<string, string> = {
  fresh: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  ageing: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  stale: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  unknown: "",
};

const pct = (v: number | null | undefined) =>
  v == null ? "—" : `${Math.round(Number(v) * 100)}%`;


function ms(v: number | null | undefined) {
  if (!v) return "—";
  return v > 1000 ? `${Math.round(v / 100) / 10}s` : `${v}ms`;
}

function renderValue(value: string | string[]) {
  if (Array.isArray(value)) {
    return (
      <ul className="list-disc pl-5 space-y-1">
        {value.map((v) => <li key={v}>{v}</li>)}
      </ul>
    );
  }
  return <p>{value}</p>;
}

/** Current registry copy for the fields the proposal touches, for the diff. */
function currentCopy(intel: CareerIntel | null, key: CareerEnrichmentFieldKey): string | string[] | null {
  if (!intel) return null;
  switch (key) {
    case "brief": return intel.brief;
    case "day_in_life": return intel.dayInLife;
    case "human_advantage": return intel.humanAdvantage;
    case "responsibilities": return intel.responsibilities;
    case "must_have": return intel.mustHave;
    case "nice_to_have": return intel.niceToHave;
    case "adjacent_roles": return intel.adjacent;
    case "entry_routes": return intel.pathways;
    case "employers": return intel.employers;
    case "progression": return intel.stretch;
    case "emerging_trends": return intel.futureSkills;
    default: return null;
  }
}

/** Bare hostname for a citation label. */
function hostOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

export default function AdminEvidenceIntelligence() {
  const { toast } = useToast();
  const [tab, setTab] = useState<TabKey>("overview");
  const [rows, setRows] = useState<EnrichmentRow[]>([]);
  const [candidates, setCandidates] = useState<EnrichmentCandidate[]>([]);
  const [stats, setStats] = useState<EnrichmentStats | null>(null);
  const [config, setConfig] = useState<EnrichmentConfig | null>(null);
  const [domains, setDomains] = useState<DomainUse[]>([]);
  const [history, setHistory] = useState<EnrichmentAuditEntry[]>([]);
  const [trust, setTrust] = useState<DomainTrustRule[]>([]);
  const [metrics, setMetrics] = useState<ReviewMetrics | null>(null);
  const [cost, setCost] = useState<DailyCost[]>([]);
  const [trace, setTrace] = useState<TraceEntry[]>([]);
  const [versions, setVersions] = useState<EnrichmentVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [review, setReview] = useState<EnrichmentRow | null>(null);
  const [reviewIntel, setReviewIntel] = useState<CareerIntel | null>(null);
  const [estimate, setEstimate] = useState<{ ids: string[]; est: RunEstimate } | null>(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [citation, setCitation] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const cancelled = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c, s, cfg, d, h, tr, m, co, tc] = await Promise.all([
        listEnrichments("career", 300),
        listEnrichmentCandidates("career", 600),
        loadEnrichmentStats("career"),
        loadEnrichmentConfig("career"),
        listTopDomains("career", 25),
        listEnrichmentAudit("career", 200),
        listDomainTrust(),
        loadReviewMetrics("career"),
        listDailyCost("career", 60),
        listEnrichmentTrace("career", undefined, 300),
      ]);
      setRows(r); setCandidates(c); setStats(s); setConfig(cfg); setDomains(d); setHistory(h);
      setTrust(tr); setMetrics(m); setCost(co); setTrace(tc);
    } catch (e) {
      toast({
        title: "Could not load the Evidence Governance console",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (!review) { setReviewIntel(null); setVersions([]); setNotes(""); setCitation(null); return; }
    let live = true;
    setNotes(review.review_notes ?? "");
    setCitation(null);
    void loadCareerIntel(review.canonical_name).then((i) => { if (live) setReviewIntel(i); });
    void listEnrichmentVersions("career", review.entity_id)
      .then((v) => { if (live) setVersions(v); })
      .catch(() => { if (live) setVersions([]); });
    return () => { live = false; };
  }, [review]);

  const forecast = useMemo(() => forecastMonthlyCost(cost), [cost]);
  const conflicted = useMemo(
    () => rows.filter((r) => (r.conflicts ?? []).length > 0),
    [rows],
  );


  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byStatus = (s: string) => rows.filter((r) => r.status === s);
    const base =
      tab === "queued" ? byStatus("queued")
      : tab === "running" ? byStatus("running")
      : tab === "completed" ? rows.filter((r) => r.status === "completed")
      : tab === "needs_review" ? rows.filter((r) => r.status === "needs_review" || r.status === "failed" || r.stale)
      : tab === "rejected" ? byStatus("rejected")
      : tab === "blocked" ? byStatus("blocked")
      : rows;
    return q ? base.filter((r) => r.canonical_name.toLowerCase().includes(q) || r.entity_id.toLowerCase().includes(q)) : base;
  }, [rows, tab, query]);

  const filteredCandidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? candidates.filter((c) => c.entity_name.toLowerCase().includes(q) || c.entity_id.toLowerCase().includes(q))
      : candidates;
  }, [candidates, query]);

  /* ---- batch management ---- */

  const askEstimate = async (ids: string[]) => {
    if (!ids.length) {
      toast({ title: "Nothing selected", description: "Pick at least one role to research." });
      return;
    }
    try {
      const res = await estimateRun({ entityIds: ids.slice(0, 25), batch: ids.length });
      const est = res.estimate ?? {
        entities: ids.length, firecrawl_searches: ids.length * 3,
        estimated_ai_tokens: ids.length * 12000, estimated_cost_usd: ids.length * 0.045,
        estimated_runtime_ms: ids.length * 20000, max_budget_usd: config?.max_budget_usd ?? 25,
      };
      setEstimate({ ids, est: { ...est, entities: ids.length, estimated_cost_usd: Math.round(ids.length * 0.045 * 1000) / 1000 } });
    } catch (e) {
      toast({
        title: "Estimate failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const approveAndRun = async () => {
    if (!estimate) return;
    const ids = estimate.ids;
    setEstimate(null);
    setRunning(true);
    cancelled.current = false;
    setProgress({ done: 0, total: ids.length });
    try {
      const out = await runBatches({
        entityIds: ids,
        chunkSize: Math.max(1, Math.min(10, config?.chunk_size ?? 5)),
        maxBudgetUsd: config?.max_budget_usd,
        shouldStop: () => cancelled.current,
        onChunk: (done, total) => setProgress({ done, total }),
      });
      toast({
        title: out.cancelled ? "Run cancelled" : "Research complete",
        description: `${out.processed} researched · ${out.promoted} promoted`,
      });
      await refresh();
    } catch (e) {
      toast({
        title: "Run failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setRunning(false);
      setProgress(null);
      setSelected(new Set());
    }
  };

  const runDue = async () => {
    setRunning(true);
    try {
      const res = await runEnrichment({ batch: config?.chunk_size ?? 5 });
      toast({ title: "Batch finished", description: res.note ?? `${res.processed ?? 0} researched` });
      await refresh();
    } catch (e) {
      toast({
        title: "Run failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setRunning(false);
    }
  };

  const persistNotes = async (row: EnrichmentRow) => {
    setSavingNotes(true);
    try {
      await saveReviewNotes("career", row.entity_id, notes);
      setRows((rs) => rs.map((r) => (r.entity_id === row.entity_id ? { ...r, review_notes: notes } : r)));
      setReview((r) => (r ? { ...r, review_notes: notes } : r));
      toast({ title: "Notes saved", description: "They travel with the export." });
    } catch (e) {
      toast({
        title: "Could not save your notes",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSavingNotes(false);
    }
  };

  /** One-click governance export: version history, provenance, contradictions, notes. */
  const exportRole = async (row: EnrichmentRow, format: "json" | "pdf") => {
    setExporting(true);
    try {
      const bundle = await buildRoleExport({ ...row, review_notes: notes || row.review_notes }, "career");
      const filename = format === "json"
        ? downloadRoleExportJson(bundle)
        : downloadRoleExportPdf(bundle);
      toast({ title: "Export ready", description: filename });
    } catch (e) {
      toast({
        title: "Export failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  /** Jump to the exact evidence passage a citation came from. */
  const jumpToCitation = (id: string) => {
    setCitation(id);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus({ preventScroll: true });
    });
  };

  const decide = async (row: EnrichmentRow, decision: "approve" | "reject" | "block") => {
    try {
      await reviewEnrichment("career", row.entity_id, decision);
      toast({
        title: decision === "approve" ? "Promoted" : decision === "reject" ? "Rejected" : "Blocked",
        description: row.canonical_name,
      });
      setReview(null);
      await refresh();
    } catch (e) {
      toast({
        title: "Review failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const togglePause = async () => {
    if (!config) return;
    try {
      await saveEnrichmentConfig("career", { paused: !config.paused });
      setConfig({ ...config, paused: !config.paused });
    } catch (e) {
      toast({
        title: "Could not change the pause state",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="min-h-dvh bg-background">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin" className="gap-1.5">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Admin hub
            </Link>
          </Button>
          <div className="flex-1 min-w-[12rem]">
            <h1 className="text-2xl font-semibold">Evidence Governance™ Console</h1>
            <p className="text-sm text-muted-foreground">
              Web evidence for emerging roles, versioned and traceable. Descriptive content only —
              psychometrics, Career DNA™, scoring and market intelligence are write-protected.
            </p>
          </div>

          <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            Reload
          </Button>
          <Button variant="outline" size="sm" onClick={() => void togglePause()} disabled={!config}>
            {config?.paused
              ? <><Play className="h-4 w-4 mr-1.5" aria-hidden="true" /> Resume</>
              : <><Pause className="h-4 w-4 mr-1.5" aria-hidden="true" /> Pause</>}
          </Button>
          <Button size="sm" onClick={() => void runDue()} disabled={running || config?.paused}>
            {running
              ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" />
              : <Play className="h-4 w-4 mr-1.5" aria-hidden="true" />}
            Run next due
          </Button>
        </div>

        {progress && (
          <div role="status" aria-live="polite" className="text-sm text-muted-foreground">
            Researching {progress.done} of {progress.total}…{" "}
            <Button variant="ghost" size="sm" onClick={() => { cancelled.current = true; }}>
              Cancel
            </Button>
          </div>
        )}

        <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
          <TabsList className="flex flex-wrap h-auto" aria-label="Evidence Governance sections">

            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key}>{t.label}</TabsTrigger>
            ))}
          </TabsList>

          <div className="mt-4">
            <Label htmlFor="eef-search" className="sr-only">Search roles</Label>
            <Input
              id="eef-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by role name or CI id"
            />
          </div>

          {/* Overview / observability */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["Candidates", candidates.length],
                ["Runs today", stats?.runs_today ?? 0],
                ["Promoted", stats?.promoted ?? 0],
                ["Needs review", stats?.needs_review ?? 0],
                ["Contradictions", conflicted.length],
                ["Avg trust", pct(metrics?.avg_trust)],
                ["Avg coverage", pct(metrics?.avg_coverage)],
                ["Spend, last 30 days", `$${forecast.last30}`],
                ["Failures", stats?.failed ?? 0],
                ["Blocked", stats?.blocked ?? 0],
                ["Avg confidence", stats?.avg_confidence ?? "—"],
                ["Avg runtime", ms(stats?.avg_runtime_ms)],
              ].map(([label, value]) => (
                <Card key={String(label)} className="p-4">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
                  <p className="text-2xl font-semibold mt-1">{String(value)}</p>
                </Card>
              ))}
            </div>
            <Card className="p-4 space-y-2 text-sm">
              <p className="font-medium flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-400" aria-hidden="true" />
                Promotion gates
              </p>
              <ul className="text-muted-foreground list-disc pl-5 space-y-1">
                <li>3+ independent de-duplicated domains and 6+ supported fields.</li>
                <li>Weighted domain trust of 0.60 or better — ten blogs never outrank one government page.</li>
                <li>Evidence coverage of 80% or better; untraceable fields are dropped before grading.</li>
                <li>Zero unresolved contradictions across sources.</li>
                <li>Every run is stored as an immutable version, promoted or not.</li>
              </ul>
              <p className="text-muted-foreground">
                Refresh every {config?.refresh_days ?? 90} days, {config?.chunk_size ?? 5} per chunk,
                budget ceiling ${config?.max_budget_usd ?? 25}. A weaker re-run lowers confidence and
                flags the row stale — it never overwrites or deletes live copy.
              </p>
            </Card>
          </TabsContent>

          {/* Contradictions */}
          <TabsContent value="conflicts" className="space-y-2">
            {conflicted.length === 0 && (
              <p className="text-sm text-muted-foreground">No contradictions detected.</p>
            )}
            <ul className="space-y-2">
              {conflicted.map((r) => (
                <li key={r.entity_id}>
                  <Card className="p-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <AlertTriangle className="h-4 w-4 text-amber-400" aria-hidden="true" />
                      <span className="flex-1 min-w-[12rem] font-medium">{r.canonical_name}</span>
                      <Badge variant="outline" className={GRADE_TONE[r.grade]}>{r.grade}</Badge>
                      <Badge variant="outline">coverage {pct(r.coverage)}</Badge>
                      <Button variant="outline" size="sm" onClick={() => setReview(r)}>Review</Button>
                    </div>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {r.conflicts.map((c) => (
                        <li key={c.axis}>
                          <span className="font-medium text-foreground">{c.axis.replace(/_/g, " ")}</span>
                          {": "}{c.claims.join(" vs ")}
                        </li>
                      ))}
                    </ul>
                  </Card>
                </li>
              ))}
            </ul>
          </TabsContent>

          {/* Domain trust: tiers, allowlist priority, blocked patterns */}
          <TabsContent value="trust" className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Weighted authority per source. Trust combines with diminishing returns, so the
              strongest source dominates — ten blogs never outweigh one government page.
            </p>
            <DomainTrustBulkEditor rules={trust} rows={rows} onSaved={refresh} />
          </TabsContent>

          {/* Cost dashboard */}
          <TabsContent value="cost" className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["Last 30 days", `$${forecast.last30}`],
                ["Per active day", `$${forecast.perDay}`],
                ["Monthly forecast", `$${forecast.forecast}`],
                ["Budget ceiling", `$${config?.max_budget_usd ?? 25}`],
                ["Firecrawl", `$${forecast.firecrawl}`],
                ["AI synthesis", `$${forecast.ai}`],
                ["Storage", `$${forecast.storage}`],
                ["Avg cost / run", `$${stats?.avg_cost_usd ?? 0}`],
              ].map(([label, value]) => (
                <Card key={String(label)} className="p-4">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
                  <p className="text-2xl font-semibold mt-1">{String(value)}</p>
                </Card>
              ))}
            </div>
            <Card className="p-3">
              <table className="w-full text-sm">
                <caption className="sr-only">Daily enrichment spend</caption>
                <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th scope="col" className="text-left py-1">Day</th>
                    <th scope="col" className="text-right py-1">Runs</th>
                    <th scope="col" className="text-right py-1">Firecrawl</th>
                    <th scope="col" className="text-right py-1">AI</th>
                    <th scope="col" className="text-right py-1">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {cost.map((c) => (
                    <tr key={c.day} className="border-t border-border/50">
                      <td className="py-1">{new Date(c.day).toLocaleDateString()}</td>
                      <td className="py-1 text-right tabular-nums">{c.runs}</td>
                      <td className="py-1 text-right tabular-nums">${c.firecrawl_usd}</td>
                      <td className="py-1 text-right tabular-nums">${c.ai_usd}</td>
                      <td className="py-1 text-right tabular-nums">${c.total_usd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {cost.length === 0 && <p className="text-sm text-muted-foreground">No spend recorded yet.</p>}
            </Card>
          </TabsContent>

          {/* Human review metrics */}
          <TabsContent value="governance" className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["Approved", metrics?.approved ?? 0],
                ["Rejected", metrics?.rejected ?? 0],
                ["Blocked", metrics?.blocked ?? 0],
                ["Pending", metrics?.pending ?? 0],
                ["Avg review time", metrics?.avg_review_seconds
                  ? `${Math.round(Number(metrics.avg_review_seconds) / 60)} min`
                  : "—"],
                ["Approved below CONFIDENT", metrics?.false_positives ?? 0],
                ["Rejected despite CONFIDENT", metrics?.false_negatives ?? 0],
                ["With contradictions", metrics?.with_conflicts ?? 0],
              ].map(([label, value]) => (
                <Card key={String(label)} className="p-4">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
                  <p className="text-2xl font-semibold mt-1">{String(value)}</p>
                </Card>
              ))}
            </div>
            <Card className="p-4 text-sm text-muted-foreground">
              Machine/human agreement is the share of CONFIDENT proposals a reviewer approved:{" "}
              {metrics && (metrics.approved + metrics.rejected) > 0
                ? pct(metrics.approved / (metrics.approved + metrics.rejected))
                : "—"}.
            </Card>
          </TabsContent>

          {/* Audit trail: candidate → research → grade → review → promotion */}
          <TabsContent value="trace" className="space-y-2">
            {trace.length === 0 && <p className="text-sm text-muted-foreground">No trace yet.</p>}
            <ul className="space-y-1 text-sm">
              {trace
                .filter((t) => {
                  const q = query.trim().toLowerCase();
                  return !q || t.entity_name?.toLowerCase().includes(q) || t.entity_id.toLowerCase().includes(q);
                })
                .map((t) => (
                  <li key={t.id} className="flex flex-wrap items-center gap-2 border-b border-border/40 py-1">
                    <History className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                    <span className="text-muted-foreground">{new Date(t.created_at).toLocaleString()}</span>
                    <Badge variant="outline">{t.action}</Badge>
                    <span className="font-medium">{t.entity_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {t.entity_id} · {t.actor_kind}
                      {t.current_version ? ` · v${t.current_version}` : ""}
                      {t.current_grade ? ` · ${t.current_grade}` : ""}
                      {t.current_promoted ? " · live" : ""}
                    </span>
                  </li>
                ))}
            </ul>
          </TabsContent>



          {/* Candidates */}
          <TabsContent value="candidates" className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm text-muted-foreground flex-1">
                {filteredCandidates.length} computed candidates · {selected.size} selected
              </p>
              <Button
                variant="outline" size="sm"
                onClick={() => setSelected(new Set(filteredCandidates.slice(0, 25).map((c) => c.entity_id)))}
              >
                Select first 25
              </Button>
              <Button size="sm" onClick={() => void askEstimate(Array.from(selected))} disabled={running}>
                Queue selected
              </Button>
              <Button
                variant="secondary" size="sm" disabled={running}
                onClick={() => void askEstimate(filteredCandidates.filter((c) => !c.promoted).map((c) => c.entity_id))}
              >
                Queue all emerging roles
              </Button>
            </div>
            <ul className="space-y-2">
              {filteredCandidates.slice(0, 300).map((c) => (
                <li key={c.entity_id}>
                  <Card className="p-3 flex flex-wrap items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-5 w-5 accent-primary"
                      checked={selected.has(c.entity_id)}
                      onChange={() => toggleSelect(c.entity_id)}
                      aria-label={`Select ${c.entity_name} for research`}
                    />
                    <span className="flex-1 min-w-[12rem]">
                      <span className="font-medium">{c.entity_name}</span>{" "}
                      <span className="text-xs text-muted-foreground">{c.entity_id} · {c.reason}</span>
                    </span>
                    {c.grade && <Badge variant="outline" className={GRADE_TONE[c.grade]}>{c.grade}</Badge>}
                    {c.promoted && <Badge variant="outline">live</Badge>}
                    <Button
                      variant="ghost" size="sm" disabled={running}
                      onClick={() => void askEstimate([c.entity_id])}
                    >
                      Research
                    </Button>
                  </Card>
                </li>
              ))}
            </ul>
          </TabsContent>

          {/* Row lists */}
          {(["queued", "running", "completed", "needs_review", "rejected", "blocked"] as TabKey[]).map((key) => (
            <TabsContent key={key} value={key} className="space-y-2">
              {filtered.length === 0 && (
                <p className="text-sm text-muted-foreground">Nothing here yet.</p>
              )}
              <ul className="space-y-2">
                {filtered.map((r) => (
                  <li key={r.entity_id}>
                    <Card className="p-3 flex flex-wrap items-center gap-3">
                      <span className="flex-1 min-w-[12rem]">
                        <span className="font-medium">{r.canonical_name}</span>{" "}
                        <span className="text-xs text-muted-foreground">
                          {r.entity_id} · v{r.version} · {r.domain_count} domains ·{" "}
                          {r.supported_fields} fields · trust {pct(r.trust_score)} ·{" "}
                          coverage {pct(r.coverage)} · confidence {r.confidence}
                        </span>
                        {r.error && <span className="block text-xs text-rose-400">{r.error}</span>}
                      </span>
                      <Badge variant="outline" className={GRADE_TONE[r.grade]}>{r.grade}</Badge>
                      {r.freshness !== "unknown" && (
                        <Badge variant="outline" className={FRESHNESS_TONE[r.freshness]}>{r.freshness}</Badge>
                      )}
                      {r.conflicts.length > 0 && (
                        <Badge variant="outline" className={GRADE_TONE.medium}>
                          {r.conflicts.length} conflict{r.conflicts.length > 1 ? "s" : ""}
                        </Badge>
                      )}
                      {r.stale && <Badge variant="outline">stale</Badge>}
                      {r.promoted && <Badge variant="outline">live</Badge>}

                      <Button variant="outline" size="sm" onClick={() => setReview(r)}>
                        Review
                      </Button>
                      <Button
                        variant="ghost" size="sm" disabled={running}
                        onClick={() => void askEstimate([r.entity_id])}
                        aria-label={`Re-run research for ${r.canonical_name}`}
                      >
                        <RotateCcw className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </Card>
                  </li>
                ))}
              </ul>
            </TabsContent>
          ))}

          {/* Sources */}
          <TabsContent value="sources" className="space-y-2">
            {domains.length === 0 && <p className="text-sm text-muted-foreground">No sources yet.</p>}
            <ul className="space-y-1">
              {domains.map((d) => (
                <li key={d.domain} className="flex items-center gap-3 text-sm">
                  <Globe className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <span className="flex-1">{d.domain}</span>
                  <span className="text-muted-foreground">{d.uses} uses</span>
                </li>
              ))}
            </ul>
          </TabsContent>

          {/* History */}
          <TabsContent value="history" className="space-y-2">
            {history.length === 0 && <p className="text-sm text-muted-foreground">No audit entries yet.</p>}
            <ul className="space-y-1 text-sm">
              {history.map((h) => (
                <li key={h.id} className="flex flex-wrap gap-2">
                  <span className="text-muted-foreground">
                    {new Date(h.created_at).toLocaleString()}
                  </span>
                  <span className="font-medium">{h.action}</span>
                  <span>{h.entity_id}</span>
                  <span className="text-muted-foreground">({h.actor_kind})</span>
                </li>
              ))}
            </ul>
          </TabsContent>
        </Tabs>
      </div>

      {/* Cost estimation + admin approval */}
      <Dialog open={!!estimate} onOpenChange={(o) => !o && setEstimate(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve this research run</DialogTitle>
            <DialogDescription>
              Nothing is spent until you approve. The budget ceiling is enforced server-side.
            </DialogDescription>
          </DialogHeader>
          {estimate && (
            <dl className="text-sm space-y-1">
              <div className="flex justify-between"><dt>Roles</dt><dd>{estimate.est.entities}</dd></div>
              <div className="flex justify-between"><dt>Firecrawl searches</dt><dd>{estimate.est.entities * 3}</dd></div>
              <div className="flex justify-between"><dt>Estimated AI tokens</dt><dd>{(estimate.est.entities * 12000).toLocaleString()}</dd></div>
              <div className="flex justify-between"><dt>Estimated cost</dt><dd>${estimate.est.estimated_cost_usd}</dd></div>
              <div className="flex justify-between"><dt>Estimated runtime</dt><dd>{ms(estimate.est.entities * 20000)}</dd></div>
              <div className="flex justify-between"><dt>Maximum budget</dt><dd>${config?.max_budget_usd ?? estimate.est.max_budget_usd}</dd></div>
            </dl>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEstimate(null)}>Cancel</Button>
            <Button onClick={() => void approveAndRun()}>Approve and run</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role review screen: current copy → proposed copy → diff → decide */}
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{review?.canonical_name}</DialogTitle>
            <DialogDescription>
              {review?.entity_id} · v{review?.version} · grade {review?.grade} · confidence{" "}
              {review?.confidence} · trust {pct(review?.trust_score)} · coverage{" "}
              {pct(review?.coverage)} · {review?.domain_count} independent domains ·{" "}
              {review?.supported_fields} supported fields
              {review?.model ? ` · ${review.model} (${review.prompt_version})` : ""}
            </DialogDescription>
          </DialogHeader>

          {review && (
            <div className="space-y-5 text-sm">
              {review.conflicts.length > 0 && (
                <section className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-1">
                  <h3 className="font-medium flex items-center gap-2 text-amber-400">
                    <AlertTriangle className="h-4 w-4" aria-hidden="true" /> Sources contradict each other
                  </h3>
                  <ul className="text-xs space-y-1">
                    {review.conflicts.map((c) => (
                      <li key={c.axis}>
                        <span className="font-medium">{c.axis.replace(/_/g, " ")}</span>:{" "}
                        {c.claims.join(" vs ")}
                        {" — "}
                        {c.sources.map((s) => (
                          <a key={s} href={s} target="_blank" rel="noopener noreferrer" className="underline mr-2">
                            {(() => { try { return new URL(s).hostname; } catch { return s; } })()}
                          </a>
                        ))}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-muted-foreground">
                    Promotion is blocked until the contradiction is resolved by a fresh run.
                  </p>
                </section>
              )}

              {(Object.keys(review.payload) as CareerEnrichmentFieldKey[]).map((key) => {
                const proposed = review.payload[key];
                if (!proposed) return null;
                const current = currentCopy(reviewIntel, key);
                return (
                  <section key={key} className="space-y-2">
                    <h3 className="font-medium">{ENRICHED_FIELD_LABELS[key] ?? key}</h3>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="rounded-lg border border-border/60 p-3">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Current</p>
                        {current && (Array.isArray(current) ? current.length > 0 : true)
                          ? renderValue(current)
                          : <p className="text-muted-foreground">Nothing today.</p>}
                      </div>
                      <div className="rounded-lg border border-primary/40 bg-primary/5 p-3">
                        <p className="text-xs uppercase tracking-wider text-primary mb-1">Proposed</p>
                        {renderValue(proposed.value)}
                        <ul className="mt-2 flex flex-wrap gap-2 text-xs">
                          {proposed.sources.map((src) => {
                            const evidenceId = `evidence-${key}-${hostOf(src)}`;
                            const hasQuote = (proposed.evidence ?? []).some((e) => e.url === src);
                            return (
                              <li key={src} className="flex items-center gap-1">
                                {hasQuote ? (
                                  <button
                                    type="button"
                                    onClick={() => jumpToCitation(evidenceId)}
                                    className="underline decoration-dotted text-primary hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                                  >
                                    {hostOf(src)}
                                  </button>
                                ) : (
                                  <a href={src} target="_blank" rel="noopener noreferrer" className="underline">
                                    {hostOf(src)}
                                  </a>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>

                    {/* Side-by-side evidence: the exact passage each citation rests on */}
                    {(proposed.evidence ?? []).length > 0 && (
                      <ul className="grid gap-2 md:grid-cols-2">
                        {(proposed.evidence ?? []).map((e) => {
                          const id = `evidence-${key}-${hostOf(e.url)}`;
                          const active = citation === id;
                          return (
                            <li
                              key={`${id}-${e.quote.slice(0, 24)}`}
                              id={id}
                              tabIndex={-1}
                              className={`rounded-lg border p-3 text-xs transition-colors ${
                                active
                                  ? "border-primary bg-primary/10 ring-2 ring-primary/40"
                                  : "border-border/60 bg-muted/20"
                              }`}
                            >
                              <p className="flex items-center gap-1.5 font-medium">
                                <Quote className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                                <a href={e.url} target="_blank" rel="noopener noreferrer" className="underline">
                                  {hostOf(e.url)}
                                </a>
                              </p>
                              <p className="mt-1 text-muted-foreground leading-relaxed">{e.quote}</p>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {(proposed.evidence ?? []).length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        No verbatim passage was captured for this field, so its citations open the source directly.
                      </p>
                    )}
                  </section>
                );
              })}

              <section className="space-y-1">
                <h3 className="font-medium">Provenance</h3>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  {review.sources.map((s) => (
                    <li key={s.url}>
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="underline">
                        {s.domain ?? s.url}
                      </a>
                      {s.trust_tier ? ` — ${String(s.trust_tier).replace(/_/g, " ")} (${s.trust_weight})` : ""}
                      {s.title ? ` — ${s.title}` : ""}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">
                  Fetched {review.fetched_at ? new Date(review.fetched_at).toLocaleString() : "—"} ·
                  last regraded {review.regraded_at ? new Date(review.regraded_at).toLocaleString() : "—"} ·
                  freshness {review.freshness} · hash {review.hash?.slice(0, 12) ?? "—"} · review{" "}
                  {review.review_status}
                </p>
              </section>

              <section className="space-y-1">
                <h3 className="font-medium">Version history</h3>
                {versions.length === 0 && (
                  <p className="text-xs text-muted-foreground">No versions recorded yet.</p>
                )}
                <ul className="space-y-1 text-xs">
                  {versions.map((v) => (
                    <li key={v.id} className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">v{v.version}</span>
                      <span className="text-muted-foreground">
                        {new Date(v.created_at).toLocaleString()}
                      </span>
                      <Badge variant="outline" className={GRADE_TONE[v.grade]}>{v.grade}</Badge>
                      <span className="text-muted-foreground">
                        trust {pct(v.trust_score)} · coverage {pct(v.coverage)} ·{" "}
                        {v.domain_count} domains · {v.supported_fields} fields
                        {v.conflicts?.length ? ` · ${v.conflicts.length} conflict(s)` : ""}
                      </span>
                      {v.promoted && <Badge variant="outline">promoted</Badge>}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">
                  Versions are append-only. Nothing here can be edited or deleted.
                </p>
              </section>
              {/* Reviewer notes travel with the governance export */}
              <section className="space-y-2">
                <Label htmlFor="review-notes" className="font-medium">Reviewer notes</Label>
                <Textarea
                  id="review-notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Why you approved, rejected or held this — included in the export."
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm" variant="secondary"
                    disabled={savingNotes || notes === (review.review_notes ?? "")}
                    onClick={() => void persistNotes(review)}
                  >
                    <Save className="h-4 w-4 mr-1.5" aria-hidden="true" /> Save notes
                  </Button>
                  <Button size="sm" variant="outline" disabled={exporting} onClick={() => void exportRole(review, "json")}>
                    <FileJson className="h-4 w-4 mr-1.5" aria-hidden="true" /> Export JSON
                  </Button>
                  <Button size="sm" variant="outline" disabled={exporting} onClick={() => void exportRole(review, "pdf")}>
                    <FileText className="h-4 w-4 mr-1.5" aria-hidden="true" /> Export PDF
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  The export carries the full version history, provenance, contradictions and these notes.
                </p>
              </section>
            </div>
          )}


          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => review && void decide(review, "block")}>
              <Ban className="h-4 w-4 mr-1.5" aria-hidden="true" /> Block
            </Button>
            <Button variant="outline" onClick={() => review && void decide(review, "reject")}>
              <X className="h-4 w-4 mr-1.5" aria-hidden="true" /> Reject
            </Button>
            <Button
              variant="secondary"
              onClick={() => review && void askEstimate([review.entity_id])}
              disabled={running}
            >
              <RotateCcw className="h-4 w-4 mr-1.5" aria-hidden="true" /> Re-run
            </Button>
            <Button
              onClick={() => review && void decide(review, "approve")}
              disabled={
                !review || review.grade !== "confident" ||
                review.conflicts.length > 0 || review.coverage < 0.8
              }
              title={
                review && review.conflicts.length > 0
                  ? "Sources contradict each other — resolve before promoting"
                  : review && review.coverage < 0.8
                  ? "Evidence coverage is below 80%"
                  : review?.grade !== "confident"
                  ? "Only CONFIDENT evidence can be promoted"
                  : undefined
              }
            >
              <Check className="h-4 w-4 mr-1.5" aria-hidden="true" /> Approve
            </Button>

          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
